import { useOverlayState } from "@heroui/react";
import { useCallback, useState } from "react";
import type { Area } from "react-easy-crop";
import Cropper from "react-easy-crop";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "@/src/common/components/ui";

interface ImageCropModalProps {
	imageSrc: string;
	aspectRatio: number;
	onCropComplete: (file: File) => void;
	onCancel: () => void;
}

async function extractCroppedBlob(imageSrc: string, pixelCrop: Area): Promise<Blob> {
	const image = await new Promise<HTMLImageElement>((resolve, reject) => {
		const img = new Image();
		img.crossOrigin = "anonymous";
		img.onload = () => resolve(img);
		img.onerror = reject;
		img.src = imageSrc;
	});

	const canvas = document.createElement("canvas");
	canvas.width = pixelCrop.width;
	canvas.height = pixelCrop.height;
	const ctx = canvas.getContext("2d");
	if (!ctx) throw new Error("Canvas context unavailable");

	ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);

	return new Promise<Blob>((resolve, reject) => {
		canvas.toBlob((blob) => {
			if (blob) resolve(blob);
			else reject(new Error("Failed to create blob"));
		}, "image/jpeg");
	});
}

export function ImageCropModal({ imageSrc, aspectRatio, onCropComplete, onCancel }: ImageCropModalProps) {
	const { t } = useTranslation("common");
	const [crop, setCrop] = useState({ x: 0, y: 0 });
	const [zoom, setZoom] = useState(1);
	const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
	const [isProcessing, setIsProcessing] = useState(false);

	const state = useOverlayState({
		isOpen: true,
		onOpenChange: (open) => {
			if (!open) onCancel();
		},
	});

	const handleCropComplete = useCallback((_croppedArea: Area, pixels: Area) => {
		setCroppedAreaPixels(pixels);
	}, []);

	const handleConfirm = async () => {
		if (!croppedAreaPixels) return;
		setIsProcessing(true);
		try {
			const blob = await extractCroppedBlob(imageSrc, croppedAreaPixels);
			const file = new File([blob], "cropped.jpg", { type: "image/jpeg" });
			onCropComplete(file);
		} finally {
			setIsProcessing(false);
		}
	};

	return (
		<Modal state={state}>
			<Modal.Backdrop>
				<Modal.Container className="max-w-lg">
					<Modal.Dialog>
						<Modal.Header>
							<h3 className="text-lg font-semibold">{t("image_crop.title")}</h3>
						</Modal.Header>
						<Modal.Body>
							<div className="relative h-72 w-full overflow-hidden rounded-lg bg-black">
								<Cropper
									image={imageSrc}
									crop={crop}
									zoom={zoom}
									aspect={aspectRatio}
									onCropChange={setCrop}
									onZoomChange={setZoom}
									onCropComplete={handleCropComplete}
								/>
							</div>
							<input
								type="range"
								min={1}
								max={3}
								step={0.01}
								value={zoom}
								onChange={(e) => setZoom(Number(e.target.value))}
								className="mt-4 w-full accent-accent"
								aria-label={t("image_crop.zoom")}
							/>
						</Modal.Body>
						<Modal.Footer className="flex justify-end gap-2">
							<Button variant="ghost" onPress={onCancel}>
								{t("image_crop.cancel")}
							</Button>
							<Button variant="primary" onPress={handleConfirm} isPending={isProcessing}>
								{t("image_crop.apply")}
							</Button>
						</Modal.Footer>
					</Modal.Dialog>
				</Modal.Container>
			</Modal.Backdrop>
		</Modal>
	);
}
